import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useMemo, useState, useEffect } from 'react';
import { getSubscriptions, getProductById } from '../../api/api';
import { useQueryClient } from '@tanstack/react-query';

// Types for subscription data
interface SubscriptionBillingInfo {
	type: 'appStore' | 'playStore' | 'stripe';
	billingId: string;
	status: 'subscribed' | 'expired' | 'active' | 'inactive' | 'cancelled';
	startDate: Date | string;
	endDate: Date | string;
	currency: string;
	price: number;
	productId: string;
	isActive: boolean;
}

interface SubscriptionProduct {
	id: string;
	name: string;
	description: string;
}

interface SubscriptionData {
	type: string;
	billingId: string;
	status: string;
	startDate: Date | string;
	endDate: Date | string;
	currency: string;
	price: number;
	productId: string;
	isActive: boolean;
	product: SubscriptionProduct;
	productName: string;
}

interface SubscriptionStatus {
	// Core subscription info
	hasActiveSubscription: boolean;
	hasAnySubscription: boolean;
	isExpired: boolean;
	isActive: boolean;
	isCancelled: boolean;

	// Subscription details
	currentSubscription: SubscriptionData | null;
	allSubscriptions: SubscriptionBillingInfo[];

	// Product info
	currentProduct: SubscriptionProduct | null;
	productName: string | null;

	// Billing info
	price: number | null;
	currency: string | null;

	// Dates
	startDate: Date | null;
	endDate: Date | null;
	isExpiringSoon: boolean; // Within 7 days

	// Status helpers
	canAccessPremiumFeatures: boolean;
	needsRenewal: boolean;

	// Raw data access
	rawBillingInfo: any;
	rawSubscriptionData: SubscriptionData | null;
}

/**
 * Global hook to get comprehensive user subscription status
 * @returns {SubscriptionStatus} Complete subscription status information
 */
export const useSubscriptionStatus = (): SubscriptionStatus => {
	const user = useSelector(
		(state: DefaultRootState) => state.promiseData.data?.userData
	);

	return useMemo(() => {
		// Extract billing info from user data
		const billingInfo = user?.billing_info || user?.billing || {};

		// Handle different billing data structures
		let subscriptions: SubscriptionBillingInfo[] = [];
		let activeSubscription: SubscriptionBillingInfo | null = null;

    const rawBillingInfo = user?.billing_info || user?.billing;

		// Ensure rawBillingInfo is an object before processing
    if (rawBillingInfo && typeof rawBillingInfo === 'object') {
      // Check if billingInfo is an object with subscription properties directly
      if (rawBillingInfo.isActive !== undefined) {
        // Single subscription object structure
        activeSubscription = rawBillingInfo as SubscriptionBillingInfo;
        subscriptions = [rawBillingInfo as SubscriptionBillingInfo];
      } else {
        // Multiple subscriptions structure (object with keys)
        subscriptions = Object.values(rawBillingInfo) as SubscriptionBillingInfo[];
        activeSubscription = subscriptions.find((sub) => sub?.isActive) || null;
      }
    }

		const hasActiveSubscription = Boolean(activeSubscription);
		const hasAnySubscription = subscriptions.length > 0;

		// Status checks
		const isExpired = activeSubscription
			? new Date(activeSubscription.endDate) < new Date()
			: false;
		const isActive = activeSubscription
			? activeSubscription.status === 'active' ||
				activeSubscription.status === 'subscribed'
			: false;
		const isCancelled = activeSubscription
			? activeSubscription.status === 'cancelled'
			: false;

		// Date calculations
		const startDate = activeSubscription
			? new Date(activeSubscription.startDate)
			: null;
		const endDate = activeSubscription
			? new Date(activeSubscription.endDate)
			: null;

		// Check if expiring soon (within 7 days)
		const isExpiringSoon = endDate
			? endDate.getTime() - new Date().getTime() < 7 * 24 * 60 * 60 * 1000
			: false;

		// Premium access logic
		const canAccessPremiumFeatures = isActive && !isExpired;
		const needsRenewal = isExpired || isExpiringSoon || isCancelled;

		// Product information
		const currentProduct = activeSubscription
			? {
					id: activeSubscription.productId,
					name: activeSubscription.productId, // Will be enhanced by API call
					description: '',
				}
			: null;

		return {
			hasActiveSubscription,
			hasAnySubscription,
			isExpired,
			isActive,
			isCancelled,
			currentSubscription: activeSubscription
				? ({
						...activeSubscription,
						product: currentProduct,
						productName:
							currentProduct?.name ||
							activeSubscription.productId,
					} as SubscriptionData)
				: null,
			allSubscriptions: subscriptions,
			currentProduct,
			productName:
				currentProduct?.name || activeSubscription?.productId || null,
			price: activeSubscription?.price || null,
			currency: activeSubscription?.currency || null,
			startDate,
			endDate,
			isExpiringSoon,
			canAccessPremiumFeatures,
			needsRenewal,
			rawBillingInfo: billingInfo,
			rawSubscriptionData: activeSubscription
				? ({
						...activeSubscription,
						product: currentProduct,
						productName:
							currentProduct?.name ||
							activeSubscription.productId,
					} as SubscriptionData)
				: null,
		};
	}, [user?.billing_info, user?.billing]);
};

/**
 * Simple hook to check if user has an active subscription
 * @returns {boolean} True if user has an active, non-expired subscription
 */
export const useHasActiveSubscription = (): boolean => {
	const { hasActiveSubscription } = useSubscriptionStatus();
	return hasActiveSubscription;
};

/**
 * Hook to check if user can access premium features
 * @returns {boolean} True if user can access premium features
 */
export const useCanAccessPremiumFeatures = (): boolean => {
	const { canAccessPremiumFeatures } = useSubscriptionStatus();
	return canAccessPremiumFeatures;
};

/**
 * Hook to check if subscription needs renewal
 * @returns {boolean} True if subscription is expired, expiring soon, or cancelled
 */
export const useNeedsRenewal = (): boolean => {
	const { needsRenewal } = useSubscriptionStatus();
	return needsRenewal;
};

/**
 * Hook to get current subscription product information
 * @returns {SubscriptionProduct | null} Current subscription product or null
 */
export const useCurrentSubscriptionProduct = (): SubscriptionProduct | null => {
	const { currentProduct } = useSubscriptionStatus();
	return currentProduct;
};

/**
 * Hook to get subscription pricing information
 * @returns {object} Price and currency information
 */
export const useSubscriptionPricing = () => {
	const { price, currency } = useSubscriptionStatus();
	return { price, currency };
};

/**
 * Hook to get subscription dates
 * @returns {object} Start and end dates
 */
export const useSubscriptionDates = () => {
	const { startDate, endDate, isExpiringSoon } = useSubscriptionStatus();
	return { startDate, endDate, isExpiringSoon };
};

/**
 * Hook to fetch fresh subscription data from API
 * @returns {Promise<SubscriptionData | string>} Fresh subscription data or error message
 */
export const useRefreshSubscriptionData = async () => {
	try {
		const data = await getSubscriptions();
		return data;
	} catch (error) {
		console.error('Failed to refresh subscription data:', error);
		throw error;
	}
};

/**
 * Hook to get enhanced subscription status with product names from React Query cache
 * This integrates with the existing subscriptionPlan query used in CompanySelector
 * @returns {SubscriptionStatus & { isLoading: boolean }} Enhanced subscription status with loading state
 */
export const useEnhancedSubscriptionStatus = () => {
	const baseSubscription = useSubscriptionStatus();
	const queryClient = useQueryClient();

	// Get subscription plan data from React Query cache (same as CompanySelector)
	const subState = queryClient.getQueryState(['subscriptionPlan']);
	const subscriptionPlan: any =
		queryClient.getQueryData(['subscriptionPlan']) || {};

	// Determine the best product name to use (without useEffect to avoid infinite loops)
	const getProductName = useMemo(() => {
		// Check multiple possible data structures
		const productName =
			subscriptionPlan?.product?.name ||
			subscriptionPlan?.name ||
			subscriptionPlan?.data?.product?.name ||
			subscriptionPlan?.data?.name ||
			subscriptionPlan?.productName; // Also check productName field directly

		if (productName === 'EXEMPT_PRODUCT') return 'SmartSaaS';
		
		// Check if productName is a product ID (starts with 'prod_')
		// If so, try to map it to a readable name
		if (productName && typeof productName === 'string' && productName.startsWith('prod_')) {
			// Map known product IDs to readable names
			const productIdMap: { [key: string]: string } = {
				prod_NRIXsLt6jURs9D: 'Premium Plan',
				prod_RCyg8ax9foK7Ki: 'Premium Plan (Dev)',
				// Add more mappings as needed - you can add prod_T4tsiEHIMHI14I here
			};

			// If we have a mapping, use it; otherwise try to get from baseSubscription
			if (productIdMap[productName]) {
				return productIdMap[productName];
			}
			
			// Fall back to baseSubscription productId mapping
			if (baseSubscription.currentSubscription?.productId) {
				return (
					productIdMap[baseSubscription.currentSubscription.productId] ||
					null // Don't show product IDs
				);
			}
			
			// If it's a product ID we don't recognize, return null to show "Free" or fallback
			return null;
		}

		// If we have a valid product name (not a product ID), return it
		if (productName && typeof productName === 'string' && !productName.startsWith('prod_')) {
			return productName;
		}

		// If we have a product ID but no name, try to get a better name from baseSubscription
		if (baseSubscription.currentSubscription?.productId) {
			// Map known product IDs to readable names
			const productIdMap: { [key: string]: string } = {
				prod_NRIXsLt6jURs9D: 'Premium Plan',
				prod_RCyg8ax9foK7Ki: 'Premium Plan (Dev)',
				// Add more mappings as needed
			};

			return (
				productIdMap[baseSubscription.currentSubscription.productId] ||
				null // Don't show product IDs
			);
		}

		return null;
	}, [
		subscriptionPlan?.product?.name,
		subscriptionPlan?.name,
		subscriptionPlan?.data?.product?.name,
		subscriptionPlan?.data?.name,
		subscriptionPlan?.productName,
		baseSubscription.currentSubscription?.productId,
	]);

	// Create enhanced product details
	const enhancedProduct = useMemo(
		() => ({
			id: baseSubscription.currentSubscription?.productId || '',
			name: getProductName || '',
			description:
				subscriptionPlan?.product?.description ||
				subscriptionPlan?.description ||
				'',
		}),
		[
			getProductName,
			baseSubscription.currentSubscription?.productId,
			subscriptionPlan?.product?.description,
			subscriptionPlan?.description,
		]
	);

	return {
		...baseSubscription,
		currentProduct: enhancedProduct,
		productName: getProductName,
		isLoading: subState?.status === 'pending',
		// Additional data from React Query
		subscriptionPlan,
		subState,
	};
};

export default useSubscriptionStatus;
